import type { InsightsOverviewResponse } from "@nlc/shared-types";

import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

import { formatMetric } from "../formatMetric";
import { Card } from "./Card";

const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

export function BestDaysToPost({
  className,
  weekdays,
}: {
  className?: string;
  weekdays: InsightsOverviewResponse["weekdays"];
}) {
  const days = MONDAY_FIRST.map((weekday) => ({
    averageViews:
      weekdays.find((day) => day.weekday === weekday)?.averageViews ?? null,
    label: dayjs().day(weekday).format("ddd"),
    weekday,
  }));
  const highest = Math.max(...days.map((day) => day.averageViews ?? 0));
  const best =
    highest > 0 ? days.find((day) => day.averageViews === highest) : undefined;
  const maxAverage = Math.max(highest, 1);

  return (
    <Card className={className} title="Best days to post">
      <p className="-mt-md text-sm text-text-tertiary">
        Average views per post, by the day it went out.
      </p>
      <ul className="flex h-48 items-end gap-sm">
        {days.map((day) => {
          const isBest = day === best;

          return (
            <li
              aria-label={`${day.label}: ${
                day.averageViews === null
                  ? "no posts"
                  : `${formatMetric(Math.round(day.averageViews))} views per post`
              }`}
              className="flex h-full flex-1 flex-col items-center justify-end gap-xs"
              key={day.weekday}
            >
              {isBest && (
                <span
                  aria-hidden
                  className="text-sm font-semibold text-text-brand-secondary"
                >
                  {formatMetric(Math.round(day.averageViews ?? 0))}
                </span>
              )}
              <div
                className={twMerge(
                  "w-full rounded-t-md bg-utility-brand-200",
                  isBest && "bg-fg-brand-primary",
                  day.averageViews === null && "bg-bg-tertiary",
                )}
                style={{
                  height: `max(4px, ${((day.averageViews ?? 0) / maxAverage) * 80}%)`,
                }}
              />
              <span aria-hidden className="text-xs text-text-tertiary">
                {day.label}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
